/**
 * IndexedDB storage service for the AIChatWidget
 * Provides persistent storage for chat messages
 */

import { logger } from "../utils/loggingUtils";

// Types from AIChatWidget
interface Message {
  id?: string; // Unique ID for deduplication
  text: string;
  type: "bot" | "user";
  timestamp: Date;
  threadId?: string | null;
}

// Storage message format (with string timestamp for storage)
interface StorageMessage {
  id: string; // Required ID for storage
  text: string;
  type: "bot" | "user";
  timestamp: string;
  threadId?: string | null | undefined;
  status?: "sent" | "pending" | "failed"; // For offline message queue
}

/**
 * Generate a cryptographically secure random string
 * Uses Web Crypto API for secure randomness
 * Guarantees exactly the requested length to prevent ID collisions
 */
const generateSecureRandomString = (length: number = 9): string => {
  if (typeof crypto === "undefined" || !crypto.getRandomValues) {
    throw new Error(
      "IndexedDBStorage: Web Crypto API not available - required for secure random generation",
    );
  }

  // Base-36 encoding of random bytes gives us ~1.29 chars per byte on average
  // Generate enough bytes to ensure we have sufficient characters
  const bytesNeeded = Math.ceil(length * 1.5);
  let result = "";

  // Keep generating until we have enough characters
  while (result.length < length) {
    const array = new Uint8Array(bytesNeeded);
    crypto.getRandomValues(array);
    const chunk = Array.from(array, (byte) => byte.toString(36)).join("");
    result += chunk;
  }

  // Return exactly the requested length
  return result.slice(0, length);
};

/**
 * Helper function to safely stringify objects for debugging
 */
const safeStringify = (obj: unknown): string => {
  // Handle null/undefined
  if (obj === null || obj === undefined) {
    return String(obj);
  }

  // Handle primitives (string, number, boolean)
  if (typeof obj === "string" || typeof obj === "number" || typeof obj === "boolean") {
    try {
      return JSON.stringify(obj);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      return `[Cannot stringify ${typeof obj}: ${errorMessage}]`;
    }
  }

  // Handle functions and symbols (unsupported types)
  if (typeof obj === "function" || typeof obj === "symbol") {
    return `[${typeof obj}: ${String(obj)}]`;
  }

  // Handle objects/arrays (typeof obj === 'object' && obj !== null)
  if (typeof obj === "object" && obj !== null) {
    try {
      // Create WeakSet to track seen objects and prevent circular references
      const seen = new WeakSet<object>();
      return JSON.stringify(
        obj,
        (key, value) => {
          // Handle Date objects by converting to ISO string
          if (value instanceof Date) {
            return value.toISOString();
          }
          // Handle objects (but not null, which is typeof 'object' in JS)
          if (typeof value === "object" && value !== null) {
            // If we've seen this object before, it's a circular reference
            if (seen.has(value)) {
              return "[Circular]";
            }
            // Add object to seen set to track for circular references
            seen.add(value);
          }
          // Return original value for JSON.stringify to serialize
          return value;
        },
        2,
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      return `[Cannot stringify: ${errorMessage}]`;
    }
  }

  // Fallback for any other type (shouldn't happen in practice)
  return `[Unknown type: ${typeof obj}]`;
};

export class IndexedDBStorage {
  private dbName = "ai-chat-widget-db";
  private dbVersion = 3;
  private db: IDBDatabase | null = null;
  private isInitializing = false;
  private initPromise: Promise<void> | null = null;

  constructor() {
    logger.log("IndexedDBStorage: Instance created");
  }

  /**
   * Check if database is already initialized
   */
  public isInitialized = (): boolean => {
    return !!this.db;
  };

  /**
   * Initialize the database
   * This should be called before any other methods
   */
  public init = async (): Promise<void> => {
    logger.log("IndexedDBStorage: Initializing database...");

    // Check if we've already initialized successfully
    if (this.db) {
      logger.log("IndexedDBStorage: Database already initialized");
      return Promise.resolve();
    }

    // If already initializing, return the existing promise
    if (this.isInitializing && this.initPromise) {
      logger.log("IndexedDBStorage: Already initializing, returning existing promise");
      return this.initPromise;
    }

    // Check for private browsing mode
    const isPrivate = await this.detectPrivateMode();
    if (isPrivate) {
      logger.warn(
        "IndexedDBStorage: Private browsing mode detected - IndexedDB may not work properly",
      );
    }

    this.isInitializing = true;
    this.initPromise = new Promise((resolve, reject) => {
      try {
        // Check that indexedDB is available
        if (!window.indexedDB) {
          logger.error("IndexedDBStorage: indexedDB not available in window");
          this.isInitializing = false;
          reject(new Error("IndexedDB not available"));
          return;
        }

        logger.log("IndexedDBStorage: Opening database:", this.dbName, "version:", this.dbVersion);

        // Safety timeout: IndexedDB open() can hang indefinitely in some browsers or under load.
        // This 10s fallback prevents perpetual initialization, clears state on timeout, and ensures
        // the promise resolves/rejects. Alternatives considered: request timeout wrappers, browser-specific checks.
        const timeoutId = setTimeout(() => {
          logger.error("IndexedDBStorage: Database open timed out after 10 seconds");
          this.isInitializing = false;
          reject(new Error("Database open timed out"));
        }, 10000);

        let request: IDBOpenDBRequest;

        try {
          request = indexedDB.open(this.dbName, this.dbVersion);
        } catch (e) {
          logger.error("IndexedDBStorage: Error creating indexedDB.open request:", e);
          clearTimeout(timeoutId);
          this.isInitializing = false;
          reject(new Error(`Failed to create indexedDB.open request: ${e}`));
          return;
        }

        // Handle database errors
        request.onerror = (event) => {
          clearTimeout(timeoutId);
          logger.error("IndexedDBStorage: Error opening database:", event);
          this.isInitializing = false;
          const error = (event.target as IDBRequest).error;
          logger.error("IndexedDBStorage: Error details:", error);
          reject(new Error(`Failed to open database: ${error?.message || "Unknown error"}`));
        };

        // Handle successful database opening
        request.onsuccess = (event) => {
          clearTimeout(timeoutId);
          this.db = (event.target as IDBOpenDBRequest).result;
          logger.log("IndexedDBStorage: Database opened successfully");

          // Add error handler to database for future errors
          this.db.onerror = (event) => {
            logger.error("IndexedDBStorage: Database error:", event);
          };

          // Verify that required object stores exist
          const storeNames = Array.from(this.db.objectStoreNames);
          logger.log("IndexedDBStorage: Available object stores:", storeNames);

          const requiredStores = ["messages"];
          const missingStores = requiredStores.filter((store) => !storeNames.includes(store));

          if (missingStores.length > 0) {
            logger.error("IndexedDBStorage: Missing required object stores:", missingStores);

            try {
              // Force a version update to recreate missing stores
              this.db.close();
              logger.log("IndexedDBStorage: Database closed, will attempt to upgrade version");

              const upgradeRequest = indexedDB.open(this.dbName, this.dbVersion + 1);

              upgradeRequest.onupgradeneeded = this.createObjectStores;

              upgradeRequest.onsuccess = () => {
                logger.log("IndexedDBStorage: Database upgraded successfully");
                this.db = upgradeRequest.result;

                // Add error handler to database for future errors
                this.db.onerror = (event) => {
                  logger.error("IndexedDBStorage: Database error after upgrade:", event);
                };

                // Verify the database structure after upgrade
                this.verifyDatabaseStructure(this.db)
                  .then(() => {
                    this.isInitializing = false;
                    resolve();
                  })
                  .catch((err) => {
                    logger.error(
                      "IndexedDBStorage: Database structure verification failed after upgrade:",
                      err,
                    );
                    this.isInitializing = false;
                    reject(new Error("Database structure verification failed"));
                  });
              };

              upgradeRequest.onerror = (upgradeEvent) => {
                logger.error("IndexedDBStorage: Error upgrading database:", upgradeEvent);
                this.isInitializing = false;
                reject(new Error("Failed to upgrade database"));
              };
            } catch (upgradeError) {
              logger.error(
                "IndexedDBStorage: Error during database upgrade attempt:",
                upgradeError,
              );
              this.isInitializing = false;
              reject(new Error(`Failed during upgrade attempt: ${upgradeError}`));
            }

            return;
          }

          // Verify the database structure
          this.verifyDatabaseStructure(this.db)
            .then(() => {
              this.isInitializing = false;
              resolve();
            })
            .catch((err) => {
              logger.error("IndexedDBStorage: Database structure verification failed:", err);
              this.isInitializing = false;
              reject(new Error("Database structure verification failed"));
            });
        };

        // Handle database upgrade/creation
        request.onupgradeneeded = this.createObjectStores;

        // Add a blocked event handler
        request.onblocked = () => {
          clearTimeout(timeoutId);
          logger.warn("IndexedDBStorage: Database open blocked - another connection is still open");
        };
      } catch (error) {
        this.isInitializing = false;
        logger.error("IndexedDBStorage: Error initializing IndexedDB:", error);
        reject(error);
      }
    });

    try {
      await this.initPromise;
      logger.log("IndexedDBStorage: Initialization completed successfully");

      // Verify data access after initialization
      await this.verifyDataExists();

      return this.initPromise;
    } catch (error) {
      logger.error("IndexedDBStorage: Initialization failed:", error);
      throw error;
    }
  };

  /**
   * Ensure the database is initialized before performing operations
   */
  private ensureInitialized = async (operation?: string): Promise<void> => {
    if (this.isInitialized()) {
      // Already initialized
      return;
    }

    logger.log(
      `IndexedDBStorage: Database not initialized for ${operation || "unknown operation"}, initializing now...`,
    );

    try {
      // Check if we're already initializing
      if (this.isInitializing && this.initPromise) {
        logger.log(
          `IndexedDBStorage: Already initializing for ${operation}, waiting for existing promise...`,
        );
        await this.initPromise;

        // Double check after initialization
        if (!this.isInitialized()) {
          logger.error(
            `IndexedDBStorage: Initialization completed but database is still not initialized`,
          );
          throw new Error("Database initialization failed");
        }

        return;
      }

      // Add a timeout to prevent hanging if initialization is stuck
      const timeoutPromise = new Promise<void>((_, reject) => {
        setTimeout(() => {
          logger.error(
            `IndexedDBStorage: Database initialization timeout for ${operation || "unknown operation"}`,
          );
          reject(new Error("Database initialization timeout"));
        }, 8000);
      });

      // Race the initialization against the timeout
      await Promise.race([this.init(), timeoutPromise]);

      // Double check that the database is really initialized
      if (!this.isInitialized()) {
        logger.error(
          `IndexedDBStorage: Database not initialized after init() for ${operation || "unknown operation"}`,
        );
        throw new Error("Database still not initialized after initialization");
      }

      logger.log(
        `IndexedDBStorage: Database successfully initialized for ${operation || "unknown operation"}`,
      );
    } catch (error) {
      logger.error(
        `IndexedDBStorage: Database initialization failed for ${operation || "unknown operation"}:`,
        error,
      );
      throw error;
    }
  };

  /**
   * Create object stores during database upgrade
   */
  private createObjectStores = (event: IDBVersionChangeEvent): void => {
    logger.log("IndexedDBStorage: Database upgrade needed, creating object stores");
    const db = (event.target as IDBOpenDBRequest).result;

    // Log existing stores before upgrade
    const existingStores = Array.from(db.objectStoreNames);
    logger.log("IndexedDBStorage: Existing stores before upgrade:", existingStores);

    // Delete threads store if it exists
    if (db.objectStoreNames.contains("threads")) {
      logger.log("IndexedDBStorage: Deleting threads store");
      db.deleteObjectStore("threads");
      logger.log("IndexedDBStorage: Threads store deleted");
    }

    // Create messages store if it doesn't exist (or recreate with proper keyPath)
    if (db.objectStoreNames.contains("messages")) {
      logger.log("IndexedDBStorage: Deleting existing messages store to update schema");
      db.deleteObjectStore("messages");
    }

    logger.log("IndexedDBStorage: Creating messages store with id keyPath");
    const store = db.createObjectStore("messages", { keyPath: "id" });
    store.createIndex("id", "id", { unique: true });
    store.createIndex("threadId", "threadId", { unique: false });
    store.createIndex("timestamp", "timestamp", { unique: false });
    store.createIndex("type", "type", { unique: false });
    logger.log("IndexedDBStorage: Messages store created successfully");

    // Log all stores after upgrade
    const updatedStores = Array.from(db.objectStoreNames);
    logger.log("IndexedDBStorage: Object stores after upgrade:", updatedStores);
  };

  /**
   * Verifies database structure has all required stores and indexes
   */
  private verifyDatabaseStructure = async (db: IDBDatabase): Promise<void> => {
    return new Promise((resolve, reject) => {
      try {
        // Check required stores
        const storeNames = Array.from(db.objectStoreNames);
        const requiredStores = ["messages"];
        const missingStores = requiredStores.filter((store) => !storeNames.includes(store));

        if (missingStores.length > 0) {
          logger.error(
            "IndexedDBStorage: Still missing required object stores after initialization:",
            missingStores,
          );
          reject(new Error(`Missing required stores: ${missingStores.join(", ")}`));
          return;
        }

        // Attempt a test transaction to verify database functionality
        const testTx = db.transaction(["messages"], "readonly");
        let testSucceeded = false;

        testTx.oncomplete = () => {
          if (testSucceeded) {
            logger.log("IndexedDBStorage: Database structure verification completed successfully");
            resolve();
          }
        };

        testTx.onerror = (event) => {
          logger.error("IndexedDBStorage: Error during test transaction:", event);
          reject(new Error("Test transaction failed"));
        };

        // Try a basic operation
        const messagesStore = testTx.objectStore("messages");
        const countRequest = messagesStore.count();

        countRequest.onsuccess = () => {
          logger.log(
            `IndexedDBStorage: Test transaction succeeded, found ${countRequest.result} messages`,
          );
          testSucceeded = true;
        };

        countRequest.onerror = (event) => {
          logger.error("IndexedDBStorage: Count request failed during test:", event);
          // Don't reject here as the transaction error handler will do it
        };
      } catch (error) {
        logger.error("IndexedDBStorage: Error verifying database structure:", error);
        reject(error);
      }
    });
  };

  /**
   * Save messages to the database
   */
  public saveMessages = async (threadId: string | null, messages: Message[]): Promise<void> => {
    // Skip empty arrays or null threadId
    if (!messages || messages.length === 0 || threadId === null) {
      logger.log("IndexedDBStorage: Skipping save - empty messages or null threadId");
      return;
    }

    logger.log(`IndexedDBStorage: Saving ${messages.length} messages with threadId=${threadId}`);
    logger.log(`IndexedDBStorage: First message sample:`, safeStringify(messages[0]));

    // Generate message IDs if they don't exist and ensure all have threadId
    const messagesWithIds = messages.map((message) => ({
      ...message,
      id: message.id || `msg_${Date.now()}_${generateSecureRandomString(9)}`,
      threadId: message.threadId || threadId,
    }));

    // Sort messages by timestamp before saving to ensure consistent order
    messagesWithIds.sort((a, b) => {
      const aTime =
        a.timestamp instanceof Date ? a.timestamp.getTime() : new Date(a.timestamp).getTime();
      const bTime =
        b.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b.timestamp).getTime();
      return aTime - bTime;
    });

    logger.log("IndexedDBStorage: Messages sorted by timestamp before saving");

    try {
      await this.ensureInitialized("saveMessages");

      return new Promise((resolve, reject) => {
        try {
          if (!this.db) {
            logger.error(
              "IndexedDBStorage: Database still not initialized after ensureInitialized",
            );
            reject(new Error("Database not initialized"));
            return;
          }

          // Create a transaction for the save operation
          const transaction = this.db.transaction(["messages"], "readwrite");

          // Add transaction event handling for better error detection
          transaction.onerror = (event) => {
            logger.error("IndexedDBStorage: Transaction error in saveMessages:", event);
            reject(
              new Error(`Transaction error: ${(event.target as IDBTransaction).error?.message}`),
            );
          };

          transaction.onabort = (event) => {
            logger.error("IndexedDBStorage: Transaction aborted in saveMessages:", event);
            reject(
              new Error(`Transaction aborted: ${(event.target as IDBTransaction).error?.message}`),
            );
          };

          // Handle transaction completion
          transaction.oncomplete = async () => {
            logger.log(`IndexedDBStorage: Successfully saved ${messagesWithIds.length} messages`);
            // Verify data after saving
            await this.verifyDataExists();
            resolve();
          };

          const messageStore = transaction.objectStore("messages");

          // First, check for existing messages with the same IDs to avoid duplicates
          let existingIds = new Set<string>();

          // Get all messages for this thread first
          const getAllRequest = messageStore.index("threadId").getAll(IDBKeyRange.only(threadId));

          getAllRequest.onsuccess = () => {
            const existingMessages: StorageMessage[] = getAllRequest.result || [];
            existingIds = new Set(existingMessages.map((msg) => msg.id));

            logger.log(
              `IndexedDBStorage: Found ${existingMessages.length} existing messages for thread`,
            );

            // Save each new message that doesn't already exist
            messagesWithIds.forEach((message, index) => {
              try {
                // Skip if this message ID already exists
                if (existingIds.has(message.id!)) {
                  logger.log(`IndexedDBStorage: Skipping duplicate message ${message.id}`);
                  return;
                }

                // Convert the message to the storage format
                const storageMessage: StorageMessage = {
                  id: message.id!,
                  text: message.text,
                  type: message.type,
                  timestamp:
                    message.timestamp instanceof Date
                      ? message.timestamp.toISOString()
                      : message.timestamp,
                  threadId: threadId,
                  status: "sent", // Default status
                };

                logger.log(
                  `IndexedDBStorage: Adding message ${index + 1}/${messagesWithIds.length}:`,
                  safeStringify(storageMessage),
                );

                // Put the message in the store
                const request = messageStore.put(storageMessage);

                request.onsuccess = () => {
                  logger.log(`IndexedDBStorage: Successfully added message ${index + 1}`);
                };

                request.onerror = (event) => {
                  logger.error(`IndexedDBStorage: Error saving message ${index + 1}:`, event);
                };
              } catch (error) {
                logger.error(
                  `IndexedDBStorage: Error processing message ${index + 1} for save:`,
                  error,
                );
              }
            });
          };

          getAllRequest.onerror = (event) => {
            logger.error("IndexedDBStorage: Error getting existing messages:", event);

            // If we can't get existing messages, try to save all messages anyway
            messagesWithIds.forEach((message, index) => {
              try {
                // Convert the message to the storage format
                const storageMessage: StorageMessage = {
                  id: message.id!,
                  text: message.text,
                  type: message.type,
                  timestamp:
                    message.timestamp instanceof Date
                      ? message.timestamp.toISOString()
                      : message.timestamp,
                  threadId: threadId,
                  status: "sent", // Default status
                };

                logger.log(
                  `IndexedDBStorage: Adding message ${index + 1}/${messagesWithIds.length} without deduplication:`,
                  safeStringify(storageMessage),
                );

                // Put the message in the store
                const request = messageStore.put(storageMessage);

                request.onsuccess = () => {
                  logger.log(`IndexedDBStorage: Successfully added message ${index + 1}`);
                };

                request.onerror = (event) => {
                  logger.error(`IndexedDBStorage: Error saving message ${index + 1}:`, event);
                };
              } catch (error) {
                logger.error(
                  `IndexedDBStorage: Error processing message ${index + 1} for save:`,
                  error,
                );
              }
            });
          };
        } catch (error) {
          logger.error("IndexedDBStorage: Error in saveMessages transaction setup:", error);
          reject(error);
        }
      });
    } catch (error) {
      logger.error("IndexedDBStorage: Error ensuring initialized for saveMessages:", error);
      throw error;
    }
  };

  /**
   * Load messages for a specific thread
   */
  public loadMessages = async (threadId: string | null): Promise<Message[]> => {
    if (!threadId) {
      logger.log("IndexedDBStorage: No threadId provided for loadMessages");
      return [];
    }

    logger.log(`IndexedDBStorage: Loading messages for threadId=${threadId}`);

    try {
      await this.ensureInitialized("loadMessages");

      return new Promise((resolve, reject) => {
        try {
          const transaction = this.db!.transaction(["messages"], "readonly");
          const store = transaction.objectStore("messages");
          const index = store.index("threadId");

          // Add timeout protection
          const timeoutId = setTimeout(() => {
            logger.error("IndexedDBStorage: loadMessages operation timed out");
            resolve([]);
          }, 5000);

          transaction.oncomplete = () => {
            clearTimeout(timeoutId);
          };

          transaction.onerror = (event) => {
            clearTimeout(timeoutId);
            logger.error("IndexedDBStorage: Error in loadMessages transaction:", event);
            reject(new Error("Failed to load messages"));
          };

          const request = index.getAll(IDBKeyRange.only(threadId));

          request.onsuccess = () => {
            clearTimeout(timeoutId);
            const storageMessages: StorageMessage[] = request.result || [];
            logger.log(`IndexedDBStorage: Loaded ${storageMessages.length} messages`);

            // Convert StorageMessages to Messages (string -> Date)
            const messages: Message[] = storageMessages.map((message) => ({
              id: message.id,
              text: message.text,
              type: message.type,
              timestamp: new Date(message.timestamp),
              threadId: message.threadId ?? null,
            }));

            // Sort messages by timestamp to ensure correct order
            messages.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

            logger.log(
              `IndexedDBStorage: Messages sorted by timestamp: ${messages.length} messages in chronological order`,
            );

            // Log the first and last message timestamps for debugging
            if (messages.length > 0) {
              const firstMessage = messages[0];
              const lastMessage = messages[messages.length - 1];
              if (firstMessage && lastMessage) {
                logger.log(
                  `IndexedDBStorage: First message time: ${firstMessage.timestamp.toISOString()}`,
                );
                logger.log(
                  `IndexedDBStorage: Last message time: ${lastMessage.timestamp.toISOString()}`,
                );
              }
            }

            resolve(messages);
          };

          request.onerror = (event) => {
            clearTimeout(timeoutId);
            logger.error("IndexedDBStorage: Error loading messages:", event);
            reject(new Error("Failed to load messages"));
          };
        } catch (error) {
          logger.error("IndexedDBStorage: Error in loadMessages:", error);
          reject(error);
        }
      });
    } catch (error) {
      logger.error("IndexedDBStorage: Error ensuring initialized for loadMessages:", error);
      return [];
    }
  };

  /**
   * Save a thread ID to the database (simplified)
   * We don't need a separate thread store anymore
   */
  public saveThreadId = async (threadId: string | null): Promise<void> => {
    if (!threadId) {
      logger.log("IndexedDBStorage: No threadId provided for saveThreadId");
      return;
    }

    logger.log(`IndexedDBStorage: Thread ID ${threadId} will be saved with messages`);
    return Promise.resolve();
  };

  /**
   * Get the most recent thread ID
   */
  public getThreadId = async (): Promise<string | null> => {
    logger.log("IndexedDBStorage: Getting latest thread ID from messages");

    try {
      await this.ensureInitialized("getThreadId");

      return new Promise((resolve, reject) => {
        try {
          const transaction = this.db!.transaction(["messages"], "readonly");
          const store = transaction.objectStore("messages");
          const index = store.index("timestamp");

          // Add timeout protection
          const timeoutId = setTimeout(() => {
            logger.error("IndexedDBStorage: getThreadId operation timed out");
            resolve(null);
          }, 5000);

          transaction.oncomplete = () => {
            clearTimeout(timeoutId);
          };

          transaction.onerror = (event) => {
            clearTimeout(timeoutId);
            logger.error("IndexedDBStorage: Error in getThreadId transaction:", event);
            resolve(null); // Resolve with null instead of rejecting to avoid crashes
          };

          // Get the most recent message by timestamp (desc order)
          const request = index.openCursor(null, "prev");

          request.onsuccess = (event) => {
            clearTimeout(timeoutId);
            const cursor = (event.target as IDBRequest).result;
            if (cursor && cursor.value.threadId) {
              logger.log(
                "IndexedDBStorage: Found latest thread from message:",
                cursor.value.threadId,
              );
              resolve(cursor.value.threadId);
            } else {
              logger.log("IndexedDBStorage: No messages with threadId found");
              resolve(null);
            }
          };

          request.onerror = (event) => {
            clearTimeout(timeoutId);
            logger.error("IndexedDBStorage: Error getting latest thread:", event);
            resolve(null); // Resolve with null instead of rejecting to avoid crashes
          };
        } catch (error) {
          logger.error("IndexedDBStorage: Error in getThreadId:", error);
          resolve(null); // Resolve with null instead of rejecting to avoid crashes
        }
      });
    } catch (error) {
      logger.error("IndexedDBStorage: Error ensuring initialized for getThreadId:", error);
      return null;
    }
  };

  /**
   * Check if there's an existing conversation
   */
  public hasExistingConversation = async (): Promise<boolean> => {
    logger.log("IndexedDBStorage: Checking for existing conversations");

    try {
      await this.ensureInitialized("hasExistingConversation");

      return new Promise((resolve, reject) => {
        try {
          const transaction = this.db!.transaction(["messages"], "readonly");
          const store = transaction.objectStore("messages");

          // Add timeout protection
          const timeoutId = setTimeout(() => {
            logger.error("IndexedDBStorage: hasExistingConversation operation timed out");
            resolve(false);
          }, 5000);

          transaction.oncomplete = () => {
            clearTimeout(timeoutId);
          };

          transaction.onerror = (event) => {
            clearTimeout(timeoutId);
            logger.error("IndexedDBStorage: Error in hasExistingConversation transaction:", event);
            resolve(false); // Resolve with false instead of rejecting to avoid crashes
          };

          const countRequest = store.count();

          countRequest.onsuccess = () => {
            clearTimeout(timeoutId);
            const hasMessages = countRequest.result > 0;
            logger.log(`IndexedDBStorage: Found ${countRequest.result} messages`);
            resolve(hasMessages);
          };

          countRequest.onerror = (event) => {
            clearTimeout(timeoutId);
            logger.error("IndexedDBStorage: Error checking for existing conversation:", event);
            resolve(false); // Resolve with false instead of rejecting to avoid crashes
          };
        } catch (error) {
          logger.error("IndexedDBStorage: Error in hasExistingConversation:", error);
          resolve(false); // Resolve with false instead of rejecting to avoid crashes
        }
      });
    } catch (error) {
      logger.error(
        "IndexedDBStorage: Error ensuring initialized for hasExistingConversation:",
        error,
      );
      return false;
    }
  };

  /**
   * Clear all messages for a specific thread
   */
  public clearMessages = async (threadId?: string | null): Promise<void> => {
    logger.log(
      `IndexedDBStorage: Clearing messages ${threadId ? "for thread " + threadId : "all"}`,
    );

    try {
      await this.ensureInitialized("clearMessages");

      return new Promise((resolve, reject) => {
        try {
          const transaction = this.db!.transaction(["messages"], "readwrite");

          transaction.oncomplete = () => {
            logger.log("IndexedDBStorage: Messages cleared successfully");
            resolve();
          };

          transaction.onerror = (event) => {
            logger.error("IndexedDBStorage: Error in clearMessages transaction:", event);
            reject(new Error("Failed to clear messages"));
          };

          const messageStore = transaction.objectStore("messages");

          if (threadId) {
            // Clear only messages for this thread
            const index = messageStore.index("threadId");
            const request = index.openCursor(IDBKeyRange.only(threadId));

            request.onsuccess = (event) => {
              const cursor = (event.target as IDBRequest).result;
              if (cursor) {
                cursor.delete();
                cursor.continue();
              }
            };
          } else {
            // Clear all messages
            messageStore.clear();
          }
        } catch (error) {
          logger.error("IndexedDBStorage: Error in clearMessages:", error);
          reject(error);
        }
      });
    } catch (error) {
      logger.error("IndexedDBStorage: Error ensuring initialized for clearMessages:", error);
      throw error;
    }
  };

  /**
   * Verify that data exists in the database
   * This diagnostic method helps troubleshoot data persistence issues
   */
  public verifyDataExists = async (): Promise<void> => {
    logger.log("IndexedDBStorage: Verifying data exists in database");

    try {
      if (!this.db) {
        logger.warn("IndexedDBStorage: Database not initialized for verification");
        return;
      }

      return new Promise((resolve, reject) => {
        try {
          // Check messages store
          const messageTx = this.db!.transaction(["messages"], "readonly");
          const messageStore = messageTx.objectStore("messages");

          // Add timeout protection
          const timeoutId = setTimeout(() => {
            logger.error("IndexedDBStorage: verifyDataExists operation timed out");
            resolve();
          }, 5000);

          messageTx.oncomplete = () => {
            clearTimeout(timeoutId);
            resolve();
          };

          messageTx.onerror = (event) => {
            clearTimeout(timeoutId);
            logger.error("IndexedDBStorage: Error verifying messages:", event);
            reject(new Error("Error verifying messages"));
          };

          const messageCountReq = messageStore.count();

          messageCountReq.onsuccess = () => {
            logger.log(`IndexedDBStorage: Found ${messageCountReq.result} messages in database`);

            // If we have messages, get a sample for debugging
            if (messageCountReq.result > 0) {
              const getAllReq = messageStore.getAll(null, 3);

              getAllReq.onsuccess = () => {
                logger.log("IndexedDBStorage: Sample messages:", safeStringify(getAllReq.result));
              };
            }
          };
        } catch (error) {
          logger.error("IndexedDBStorage: Error in verifyDataExists:", error);
          resolve(); // Resolve anyway to prevent blocking operations
        }
      });
    } catch (error) {
      logger.error("IndexedDBStorage: Error in verifyDataExists:", error);
    }
  };

  /**
   * Detect if browser is in private/incognito mode
   */
  private async detectPrivateMode(): Promise<boolean> {
    try {
      // Try to use localStorage as a quick test
      localStorage.setItem("test", "1");
      localStorage.removeItem("test");

      // If localStorage works, check IndexedDB storage quota
      if (navigator.storage && navigator.storage.estimate) {
        const estimate = await navigator.storage.estimate();
        const quota = estimate.quota || 0;

        // In private mode, browsers often limit storage to <120MB
        if (quota < 120 * 1024 * 1024) {
          logger.warn("IndexedDBStorage: Low storage quota detected, possibly in private mode");
          return true;
        }
      }

      return false;
    } catch (e) {
      // If localStorage fails, we're likely in private mode
      logger.warn("IndexedDBStorage: Storage access error, likely in private mode");
      return true;
    }
  }
}

// Create a singleton instance
export const indexedDBStorage = new IndexedDBStorage();
